// Query keys — moteur de workflows (EPIC 12, WKF-001→015).
//
// ⚠️ Le LISTING (WorkflowsListPage) passe par <DataTable mode="server"> qui gère
// ses PROPRES clés (chaîne d'endpoint brute `['/api/workflows', params]` via
// useDataTableServer). Après une mutation (activate/pause/bulk/delete), invalider
// EN PLUS de `workflows.stats()` la clé DataTable via datatable-invalidate.ts.
// Les clés ci-dessous couvrent tout le RESTE (détail éditeur, catalogues meta,
// historique, calendrier, natifs, satisfaction).

const workflowsAll = ['workflows'] as const
const workflowRunsAll = ['workflow-runs'] as const

export const workflowsKeys = {
    workflows: {
        all: workflowsAll,
        // Encart statistiques du listing (GET /api/workflows/stats).
        // ⚠️ L'ENTITÉ fait partie de la clé : la réponse est scopée sur le
        // périmètre visible. Sans elle, changer d'entité servait les chiffres de la
        // précédente pendant le rafraîchissement (même défaut que `eventCatalog`,
        // corrigé le 2026-08-13).
        stats: (entityId: string) => [...workflowsAll, 'stats', entityId] as const,
        /** Préfixe d'INVALIDATION : purge les chiffres de toutes les entités d'un
         *  coup (patron de `nativesAll`). Ne jamais l'utiliser pour LIRE. */
        statsAll: () => [...workflowsAll, 'stats'] as const,
        // Agrégat éditeur (GET /api/workflows/{id} : workflow + conditions + exceptions + outcomes).
        detail: (id: string) => [...workflowsAll, 'detail', id] as const,
        // Séquence d'actions d'un workflow (GET /api/workflows/{id}/outcomes).
        outcomes: (id: string) => [...workflowsAll, 'outcomes', id] as const,
        // Catalogue des event_code sélectionnables (vivants/dormants) — GET /event-catalog.
        // L'entité fait PARTIE de la clé : la réponse porte les valeurs propres à
        // l'organisme (référentiels Qualité, secteurs/familles/groupes du catalogue).
        // Sans elle, changer d'entité laissait afficher les listes de la précédente
        // le temps que l'invalidation globale rende la main.
        // La LANGUE de l'interface en fait partie aussi : le serveur écrit le libellé et
        // l'aide des champs personnalisés dans la langue demandée (`?lang=`), et changer
        // de langue relit le catalogue au lieu de servir les libellés de l'autre.
        eventCatalog: (entityId: string, langue: string) =>
            [...workflowsAll, 'event-catalog', entityId, langue] as const,
        /** Préfixe d'INVALIDATION de toutes les entités (patron de `statsAll`) : un champ
         *  personnalisé créé chez une entité mère entre au catalogue de ses filles. */
        eventCatalogAll: () => [...workflowsAll, 'event-catalog'] as const,
        // Registre des champs conditionnables pour un event_code — GET /field-registry.
        fieldRegistry: (eventCode: string) =>
            [...workflowsAll, 'field-registry', eventCode] as const,
        // Métadonnées des types d'action + variables substituables (WKF-006).
        outcomeTypes: () => [...workflowsAll, 'meta', 'outcome-types'] as const,
        // Balises d'un déclencheur. L'ENTITÉ et la LANGUE en font partie (CUSTOM-014) : les
        // champs personnalisés offerts sont ceux que voit l'entité, libellés dans la langue
        // de l'interface. Sans elles, changer d'entité offrait les champs de la précédente.
        variables: (entityId: string, trigger: string | null, langue: string) =>
            [...workflowsAll, 'meta', 'variables', entityId, trigger, langue] as const,
        /** Préfixe d'INVALIDATION de toutes les entités : un champ créé, renommé ou désactivé. */
        variablesAll: () => [...workflowsAll, 'meta', 'variables'] as const,
        // Catalogue des automatisations groupées par thème (WKF-012) — GET /natives.
        // `includeCustom` distingue la vue de PILOTAGE du menu (tout ce que
        // l'organisme a) de la galerie de PERSONNALISATION des Réglages (modèles
        // Argalis seuls) : deux réponses différentes, donc deux entrées de cache.
        // ⚠️ L'ENTITÉ fait partie de la clé, comme pour `stats` et `eventCatalog` : la
        // réponse est servie pour UNE entité (`WHERE entity_id = $1` côté serveur).
        // Sans elle, `invalidateQueries` laisse la donnée en place pendant le refetch
        // et la galerie — vue par DÉFAUT, et seul écran d'automatisation des plans
        // FOCUS/BOOST — affichait les modèles de l'entité précédente sous le nom de la
        // nouvelle, boutons vivants. Corrigé le 2026-08-14.
        natives: (entityId: string, includeCustom = false) =>
            [...workflowsAll, 'natives', entityId, includeCustom] as const,
        // Racine d'invalidation des DEUX variantes ci-dessus.
        nativesAll: () => [...workflowsAll, 'natives'] as const,
        // Génération d'une proposition IA (WKF-016) — POST /ai/draft. Clé de
        // MUTATION : chaque génération est un acte avec son coût, jamais un cache.
        aiDraft: () => [...workflowsAll, 'ai', 'draft'] as const,
        // Calendrier prévisionnel (WKF-010) — fenêtre [from,to) + filtres.
        // L'entité en fait partie pour la même raison que `natives` : la fenêtre est
        // servie sur le périmètre visible, et deux entités n'ont pas le même agenda.
        calendar: (entityId: string, range: Record<string, unknown>) =>
            [...workflowsAll, 'calendar', entityId, range] as const,
        // KPIs live 30j d'un workflow (WKF-009).
        kpis: (id: string) => [...workflowsAll, 'kpis', id] as const,
        // Journal des modifications (audit_trail proxy) d'un workflow (WKF-009).
        changelog: (id: string) => [...workflowsAll, 'changelog', id] as const,
        // Catalogues satisfaction (WKF-015) — catégories & configs de relance.
        satisfactionCategories: () =>
            [...workflowsAll, 'meta', 'satisfaction-categories'] as const,
        satisfactionReminderConfigs: (entityId: string) =>
            [...workflowsAll, 'meta', 'satisfaction-reminder-configs', entityId] as const,
    },
    // Historique global des exécutions (WKF-009) — GET /api/automation/workflow-runs.
    workflowRuns: {
        all: workflowRunsAll,
        list: (filters?: Record<string, unknown>) =>
            [...workflowRunsAll, 'list', filters] as const,
        detail: (id: string) => [...workflowRunsAll, 'detail', id] as const,
    },
}
